import { useMemo } from 'react';

import { useAuth } from '@/lib/auth/AuthContext';
import { useEdition } from '@/lib/auth/useEdition';
import { type NavGroup } from '@/config/navigation';
import { useOrganization } from '@/features/members';
import { usePermissions } from '@/features/permissions';
import { useV2ImportAccess } from '@/features/v2-import/api/v2-import-api';

export function useFilteredNavigation(navGroups: NavGroup[]): NavGroup[] {
  const { can, canAny } = usePermissions();
  const edition = useEdition();
  const { user } = useAuth();
  const { data: org } = useOrganization();
  const { data: importAccess } = useV2ImportAccess();
  const hidden = org?.settings?.hidden_modules;
  const isSuperuser = !!user?.is_superuser;
  const canImportFromV2 = !!importAccess?.can_import;

  return useMemo(() => {
    return navGroups
      .filter((group) => !group.requiredEdition || group.requiredEdition === edition)
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const module = (
            {
              '/claims': 'billing',
              '/imaging': 'imaging',
              '/calendar': 'calendar',
              '/eligibility': 'billing',
              '/upload-schedule': 'upload_schedule',
            } as Record<string, string>
          )[item.href];
          if (module && Array.isArray(hidden) && hidden.includes(module)) return false;
          if (item.requireSuperuser && !isSuperuser) {
            return false;
          }
          if (item.requireImportAccess && !canImportFromV2) {
            return false;
          }
          if (item.requiredEdition && item.requiredEdition !== edition) {
            return false;
          }
          if (item.requiredPermission) {
            return can(item.requiredPermission);
          }
          if (item.requiredPermissions) {
            return canAny(...item.requiredPermissions);
          }
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [navGroups, can, canAny, edition, isSuperuser, canImportFromV2, hidden]);
}
